import { Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { reviews } from "@/lib/siteConfig";
import { commonContent } from "@/cms/cmsModel";

const GoogleG = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Stars = ({ count = 5 }) => (
  <div className="flex items-center gap-0.5 text-[#C99A3D]">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={16} fill={i < count ? "#C99A3D" : "none"} className={i < count ? "" : "text-[#CBD6E2]"} />
    ))}
  </div>
);

export const ReviewsSection = () => {
  const copy = commonContent.reviews;
  return (
    <section className="relative overflow-hidden bg-[#E7EDF5] py-16 lg:py-20" data-testid="reviews-section">
      <img
        src={copy.background}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      {/* Darkening scrim so the white cards + headings stay legible over the
          bright showroom image */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "rgba(12,16,20,0.55)" }}
      />
      <div className="relative az-container">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-4">
            <SectionHeading
              eyebrow={copy.eyebrow}
              title={copy.title}
              tone="dark"
            />
            <Reveal className="mt-6 rounded-2xl border border-[#D5DEE8] bg-white p-6 shadow-[var(--shadow-soft)]">
              <div className="flex items-center gap-3">
                <GoogleG size={30} />
                <div>
                  <div className="flex items-baseline gap-1">
                    <span
                      className="font-heading text-4xl font-bold text-[#193D6D]"
                      data-testid="reviews-rating-text"
                    >
                      {reviews.rating}
                    </span>
                    <span className="text-lg text-[#6e7683]">/ {reviews.scale}</span>
                  </div>
                  <Stars count={5} />
                </div>
              </div>
              <p className="mt-4 text-[#586576]">
                {reviews.rating}/{reviews.scale} basierend auf{" "}
                <span className="font-semibold text-[#193D6D]">{reviews.count} Bewertungen</span>.
                {copy.closing}
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <div className="grid gap-5 sm:grid-cols-2" data-testid="reviews-grid">
              {reviews.items.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.12}>
                  <figure
                    className="flex h-full flex-col rounded-2xl border border-[#D5DEE8] bg-white p-6 shadow-[var(--shadow-soft)] transition-all duration-[350ms] ease-out hover:-translate-y-1 hover:border-[#7A1516]/20 hover:shadow-[var(--shadow-card)]"
                    data-testid="reviews-card"
                  >
                    <Stars count={r.stars} />
                    <blockquote className="mt-3 flex-1 text-[#2f3a49] [overflow-wrap:anywhere]">„{r.text}“</blockquote>
                    <figcaption className="mt-4 flex items-center gap-3 border-t border-[#E7EDF5] pt-4">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#6e7683]/15 font-heading text-lg font-bold text-[#6e7683]">
                        {r.name.charAt(0)}
                      </span>
                      <span>
                        <span className="block font-semibold text-[#193D6D]">{r.name}</span>
                        <span className="block text-sm text-[#6e7683]">{r.city}</span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
